import { useMemo, type ReactNode } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useQueries } from '@tanstack/react-query';
import { FileTextIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { datasourceEndpoints, documentsEndpoints } from '@/api/endpoints';
import { callGet } from '@/api/fetcher';
import useCollections from '@/hooks/useCollections';
import { filterFiltersByUrl } from '@/modules/analytics/constants';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { ratingsFilterToSet } from '@/modules/analytics/utils/expandRatings';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';
import { documentLink } from '@/modules/collections/utils/documentLink';
import { selectAccountId, selectAccountSlug } from '@/redux/session/selectors';
import { AccountAnalytics } from '@/shared/types/analytics';
import { Datasource, Datasources, Document } from '@/shared/types/collections';
import { getAnalyticsRangeUTC } from '@/util/analytics';
import { getGraphQLSet } from '@/util/util';

import { useComparisonMode } from '../../../hooks/useComparisonMode';

export interface KnowledgeBaseRow {
  id: string;
  documentId: string;
  collectionId: string;
  title: string | null;
  icon: ReactNode;
  collection: string | null;
  link: string | null;
  retrieved: number;
  used: number;
  previousUsed: number;
}

interface KbPerformanceRow {
  collection_id: string;
  datasource_id: string;
  document_id: string;
  retrievals: number | null;
  citations: number | null;
}

interface KbPerformanceVariables {
  start_time: string | null;
  end_time: string | null;
  // _uuid / _text / _int4 SQL-function args: serialize via getGraphQLSet to a
  // Postgres array literal string ("{a,b}"), never a raw JSON array.
  desk_ids: string | null;
  brain_parent_ids: string | null;
  channels: string | null;
  csat_brain: string | null;
  is_proactive?: boolean;
  is_covered?: boolean;
  is_test?: boolean;
}

const LOOKUP_STALE_TIME_MS = 2 * 60 * 1000;
const MAX_ROWS = 10;

const GET_AI_AGENTS_RETRIEVALS_BY_DOCUMENT = gql`
  query AiAgentsRetrievalsByDocument(
    $start_time: timestamp!
    $end_time: timestamp!
    $desk_ids: _uuid
    $channels: _text
    $brain_parent_ids: _uuid
    $is_proactive: Boolean
    $is_covered: Boolean
    $csat_brain: _int4
    $is_test: Boolean
  ) {
    rows: ai_agents_retrievals_by_document(
      args: {
        start_time: $start_time
        end_time: $end_time
        desk_ids: $desk_ids
        channels: $channels
        brain_parent_ids: $brain_parent_ids
        is_proactive: $is_proactive
        is_covered: $is_covered
        csat_brain: $csat_brain
        is_test: $is_test
      }
    ) {
      collection_id
      datasource_id
      document_id
      retrievals
      citations
    }
  }
`;

const rowKey = (row: Pick<KbPerformanceRow, 'datasource_id' | 'document_id'>) =>
  `${row.datasource_id}:${row.document_id}`;

const documentKey = (datasourceId: string, documentId: string) => `${datasourceId}:${documentId}`;

interface BuildKbPerformanceVariablesArgs {
  analyticsFilters: AccountAnalytics[];
  pathname: string;
  startDate: string | undefined;
  endDate: string | undefined;
}

export const buildKbPerformanceVariables = ({
  analyticsFilters,
  pathname,
  startDate,
  endDate,
}: BuildKbPerformanceVariablesArgs): KbPerformanceVariables => {
  const range = getAnalyticsRangeUTC(startDate, endDate);
  const vars: KbPerformanceVariables = {
    start_time: range.startDate,
    end_time: range.endDate,
    desk_ids: null,
    brain_parent_ids: null,
    channels: null,
    csat_brain: null,
  };

  const allowed = analyticsFilters.filter((f) => filterFiltersByUrl(f.type, pathname));
  for (const filter of allowed) {
    if (filter.type === 'firstMessageType') {
      if (filter.firstMessageType === 'proactive') {
        vars.is_proactive = true;
      } else if (filter.firstMessageType === 'reactive') {
        vars.is_proactive = false;
      }
      continue;
    }
    if (filter.type === 'ratings') {
      vars.csat_brain = ratingsFilterToSet(filter.ratings, filter.ratingsMode);
      continue;
    }
    if (filter.type === 'deskIds') {
      vars.desk_ids = getGraphQLSet(filter.deskIds?.map((o) => o.value) ?? []);
      continue;
    }
    if (filter.type === 'brainIds') {
      vars.brain_parent_ids = getGraphQLSet(filter.brainIds?.map((o) => o.value) ?? []);
      continue;
    }
    if (filter.type === 'channels') {
      vars.channels = getGraphQLSet(filter.channels ?? []);
      continue;
    }
    if (filter.type === 'isTest') {
      vars.is_test = filter.isTest;
      continue;
    }
  }

  return vars;
};

const fetchDatasources = (collectionId: string): Promise<Datasources> =>
  callGet(datasourceEndpoints.datasources(collectionId));

const fetchDocument = (
  collectionId: string,
  datasourceId: string,
  documentId: string
): Promise<Document> =>
  callGet(documentsEndpoints.document(collectionId, datasourceId, documentId));

export const useKnowledgeBasePerformance = () => {
  const { isComparing } = useComparisonMode();
  const filters = useSelector(selectFilters);
  const accountId = useSelector(selectAccountId);
  const slug = useSelector(selectAccountSlug);
  const location = useLocation();
  const { collections, listStatus: collectionsStatus } = useCollections();

  const comparisonDates = useMemo(
    () => getComparisonDates(filters.startDate, filters.endDate),
    [filters.startDate, filters.endDate]
  );

  const currentVariables = useMemo(
    () =>
      buildKbPerformanceVariables({
        analyticsFilters: filters.analytics,
        pathname: location.pathname,
        startDate: filters.startDate,
        endDate: filters.endDate,
      }),
    [filters.analytics, filters.startDate, filters.endDate, location.pathname]
  );

  const comparisonVariables = useMemo(
    () =>
      buildKbPerformanceVariables({
        analyticsFilters: filters.analytics,
        pathname: location.pathname,
        startDate: comparisonDates.startDate,
        endDate: comparisonDates.endDate,
      }),
    [filters.analytics, comparisonDates.startDate, comparisonDates.endDate, location.pathname]
  );

  const skip =
    !accountId ||
    !filters.filtersLoaded ||
    !currentVariables.start_time ||
    !currentVariables.end_time;

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery<{ rows: KbPerformanceRow[] }>(GET_AI_AGENTS_RETRIEVALS_BY_DOCUMENT, {
    variables: currentVariables,
    skip,
  });

  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery<{ rows: KbPerformanceRow[] }>(GET_AI_AGENTS_RETRIEVALS_BY_DOCUMENT, {
    variables: comparisonVariables,
    skip: skip || !isComparing || !comparisonVariables.start_time || !comparisonVariables.end_time,
  });

  const collectionNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of collections ?? []) {
      map.set(c.collection_id, c.name);
    }
    return map;
  }, [collections]);

  const topCurrentRows = useMemo(() => {
    const rows = [...(currentData?.rows ?? [])];
    rows.sort((a, b) => (b.citations ?? 0) - (a.citations ?? 0));
    return rows.slice(0, MAX_ROWS);
  }, [currentData?.rows]);

  const documentLookupTriples = useMemo(() => {
    const seen = new Set<string>();
    const triples: { collectionId: string; datasourceId: string; documentId: string }[] = [];
    for (const row of topCurrentRows) {
      const key = `${row.collection_id}:${row.datasource_id}:${row.document_id}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      triples.push({
        collectionId: row.collection_id,
        datasourceId: row.datasource_id,
        documentId: row.document_id,
      });
    }
    return triples;
  }, [topCurrentRows]);

  const documentQueries = useQueries({
    queries: documentLookupTriples.map(({ collectionId, datasourceId, documentId }) => ({
      queryKey: [documentsEndpoints.document(collectionId, datasourceId, documentId)],
      queryFn: () => fetchDocument(collectionId, datasourceId, documentId),
      staleTime: LOOKUP_STALE_TIME_MS,
      enabled: !!accountId,
    })),
  });

  const missingDocumentKeys = useMemo(() => {
    const missing = new Set<string>();
    documentQueries.forEach((q, index) => {
      const triple = documentLookupTriples[index];
      if (!triple || !q.isSuccess || q.data) {
        return;
      }
      missing.add(documentKey(triple.datasourceId, triple.documentId));
    });
    return missing;
  }, [documentQueries, documentLookupTriples]);

  const documentsByKey = useMemo(() => {
    const map = new Map<string, Document>();
    for (const q of documentQueries) {
      if (q.data) {
        map.set(documentKey(q.data.datasource_id, q.data.document_id), q.data);
      }
    }
    return map;
  }, [documentQueries]);

  const documentLookupLoading = documentQueries.some((q) => q.isLoading);

  const lookupCollectionIds = useMemo(
    () => [...new Set(documentLookupTriples.map((t) => t.collectionId))],
    [documentLookupTriples]
  );

  const datasourceQueries = useQueries({
    queries: lookupCollectionIds.map((collectionId) => ({
      queryKey: [datasourceEndpoints.datasources(collectionId)],
      queryFn: () => fetchDatasources(collectionId),
      staleTime: LOOKUP_STALE_TIME_MS,
      enabled: !!accountId,
    })),
  });

  const datasourceLookupLoading = datasourceQueries.some((q) => q.isLoading);

  const datasourcesById = useMemo(() => {
    const map = new Map<string, Datasource>();
    for (const q of datasourceQueries) {
      for (const datasource of q.data?.datasources ?? []) {
        map.set(datasource.datasource_id, datasource);
      }
    }
    return map;
  }, [datasourceQueries]);

  const data = useMemo<KnowledgeBaseRow[]>(() => {
    const previousByKey = new Map<string, KbPerformanceRow>(
      (comparisonData?.rows ?? []).map((row) => [rowKey(row), row])
    );

    return topCurrentRows.map((row) => {
      const id = rowKey(row);
      const doc = documentsByKey.get(documentKey(row.datasource_id, row.document_id));

      const datasource = datasourcesById.get(row.datasource_id);
      const collectionName = collectionNameById.get(row.collection_id);
      const documentMissing = missingDocumentKeys.has(
        documentKey(row.datasource_id, row.document_id)
      );

      return {
        id,
        documentId: row.document_id,
        collectionId: row.collection_id,
        title: doc?.name ?? (documentMissing ? null : row.document_id),
        icon: <FileTextIcon />,
        collection: collectionName ?? (collectionsStatus === 'success' ? null : row.collection_id),
        link:
          slug && datasource && doc
            ? documentLink(slug, row.collection_id, row.datasource_id, row.document_id, datasource)
            : null,
        retrieved: row.retrievals ?? 0,
        used: row.citations ?? 0,
        previousUsed: previousByKey.get(id)?.citations ?? 0,
      };
    });
  }, [
    topCurrentRows,
    comparisonData?.rows,
    documentsByKey,
    missingDocumentKeys,
    collectionNameById,
    collectionsStatus,
    datasourcesById,
    slug,
  ]);

  return {
    data,
    isLoading:
      currentLoading ||
      (isComparing && comparisonLoading) ||
      documentLookupLoading ||
      datasourceLookupLoading,
    error: currentError ?? comparisonError ?? null,
  };
};
